import type { UserDto } from "@babackapp/private-api-client/types";
import {
	useFormErrorNotification,
	useFormSuccessNotification,
} from "@babackapp/react-hook-forms-components/form-notifications";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@babackapp/ui/components/avatar";
import { Button } from "@babackapp/ui/components/button";
import { useMutation } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { type ChangeEvent, useCallback, useRef } from "react";
import config from "@/config.ts";

interface EditPictureProfileFormProps {
	user: UserDto;
}

interface UploadProfilePictureParams {
	file: File;
}

async function uploadProfilePicture(params: UploadProfilePictureParams) {
	const formData = new FormData();
	formData.append("file", params.file);

	const response = await fetch(`${config.api.url}/users/me/logo`, {
		method: "POST",
		body: formData,
	});

	if (!response.ok) {
		throw new Error("Failed to upload profile picture");
	}
}

function useUploadProfilePicture() {
	const successNotification = useFormSuccessNotification();
	const errorNotification = useFormErrorNotification();

	return useMutation({
		mutationFn: uploadProfilePicture,
		onSuccess() {
			successNotification();
		},
		onError() {
			errorNotification();
		},
	});
}

export default function EditPictureProfileForm({
	user,
}: EditPictureProfileFormProps) {
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const uploadProfilePicture = useUploadProfilePicture();

	const handleFileChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];

			if (file) {
				const allowedFormats = ["image/jpeg", "image/png"];
				if (!allowedFormats.includes(file.type)) {
					alert(
						"Format non supporté. Veuillez télécharger une image au format JPEG ou PNG.",
					);
					return;
				}

				const maxSizeInMB = 8;
				if (file.size > maxSizeInMB * 1024 * 1024) {
					alert(
						`L'image est trop volumineuse. Taille maximale autorisée : ${maxSizeInMB} Mo.`,
					);
					return;
				}

				const image = new Image();
				image.src = URL.createObjectURL(file);

				image.onload = () => {
					const { width, height } = image;

					if (width > 400 || height > 400) {
						alert(
							`Dimensions recommandées : 400 x 400 pixels. Dimensions actuelles : ${width} x ${height}.`,
						);
						return;
					}

					uploadProfilePicture.mutate({ file });
					URL.revokeObjectURL(image.src);
				};

				image.onerror = () => {
					alert(
						"Erreur lors du chargement de l'image. Veuillez réessayer avec un autre fichier.",
					);
				};
			}
		},
		[],
	);

	const handleEditClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	return (
		<div className={"flex items-center justify-center"}>
			<div className={"relative group"}>
				<Avatar className={"h-24 w-24"}>
					<AvatarImage alt={"avatar"} src={user.profile?.logoUrl} />
					<AvatarFallback>{user.profile?.firstName?.charAt(0)}</AvatarFallback>
				</Avatar>
				<Button
					className={
						"absolute bottom-0 right-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-10 w-10"
					}
					onClick={handleEditClick}
				>
					<Pencil className={"w-4 h-4"} />
				</Button>
				<input
					type={"file"}
					accept={".jpeg,.jpg,.png"}
					ref={fileInputRef}
					className={"hidden"}
					onChange={handleFileChange}
				/>
			</div>
		</div>
	);
}
